import { mapActions, mapGetters } from 'vuex'
import SectionImage from '../../calendesk/models/DTO/Response/SectionImage'
import Section from '@/calendesk/models/DTO/Response/Section'
import store from '@/store'
import { DraftActionsType } from '@/calendesk/models/DraftActionsType'
import { DraftConfiguration } from '@/calendesk/models/DraftConfiguration'
import { DraftConfigurationType } from '@/calendesk/models/BuilderTypes'
import mixins from 'vue-typed-mixins'
import DraftElement from '@/builder/sections/mixins/DraftElement'
import { SectionImageType } from '@/calendesk/models/DTO/Response/SectionImageType'
import SectionToUpdate from '@/calendesk/models/SectionToUpdate'
import { v4 as uuidv4 } from 'uuid'

export default mixins(DraftElement).extend({
  data () {
    return {
      configuration: Array<DraftConfiguration>(),
      tools: Array<DraftConfigurationType>(),
      mode: DraftActionsType.CONFIGURATION,
      // Edits buffered between flushes. A second field changed inside the
      // window joins the buffer instead of replacing the first field's change,
      // and one flush becomes one store commit and one undo step.
      pendingProperties: {} as Record<string, any>,
      pendingFlushTimer: 0,
      elementsRequireLogin: [
        'wb_login_modal__external_configuration__',
        'wb_sign_up_modal__external_configuration__',
        'wb_forgotten_password_modal__external_configuration__',
        'wb_forgotten_password_confirmation_modal__external_configuration__'
      ]
    }
  },
  computed: {
    ...mapGetters({
      draft: 'builder/getDraft',
      selectedSection: 'builder/getSelectedSection',
      selectedExternalConfiguration: 'builder/getSelectedExternalConfiguration'
    }),
    singleImageItems (): Array<SectionImage> {
      return this.selectedSection.images.filter((image: SectionImage) => image.type === SectionImageType.IMAGE)
    },
    portfolioImages (): Array<SectionImage> {
      return this.selectedSection.images.filter((image: SectionImage) => image.type === SectionImageType.PORTFOLIO)
    },
    internalImageItems (): Array<SectionImage> {
      return this.selectedSection.images.filter((image: SectionImage) => image.type === SectionImageType.INTERNAL)
    },
    usesPortfolioImages (): boolean {
      return Boolean(this.selectedSection.configuration.has_portfolio_images)
    },
    hasInternalImages (): boolean {
      return Boolean(this.selectedSection.configuration.has_internal_images)
    },
    hasInternalHtmlContent (): boolean {
      return Boolean(this.selectedSection.configuration.has_internal_html_content)
    },
    hasSectionFeatureSubtitle (): boolean {
      return Boolean(this.selectedSection.configuration.has_section_feature_subtitle)
    },
    hideSectionFeatureImage (): boolean {
      return Boolean(this.selectedSection.configuration.wb_image_hide__checkbox__)
    }
  },
  watch: {
    draft () {
      this.refreshConfiguration(this.tools)
    },
    // Buffered edits belong to the selection they were typed into - flush them
    // against the OLD target before the panel rebuilds for the new one.
    selectedSection (newSection: Section, oldSection: Section) {
      this.flushPendingProperties(oldSection)
      this.refreshConfiguration(this.tools)
    },
    selectedExternalConfiguration (newConfiguration: Record<string, any>, oldConfiguration: Record<string, any>) {
      this.flushPendingProperties(null, oldConfiguration)
      this.refreshConfiguration(this.tools)
    }
  },
  mounted () {
    this.refreshConfiguration(this.tools)
  },
  beforeDestroy () {
    this.flushPendingProperties()
  },
  methods: {
    ...mapActions({
      openSidebarDetails: 'sidebar/openSidebarDetails'
    }),
    tooltipFor (config: DraftConfiguration): string | null {
      const key = `${config.trans}_tooltip`

      if (!this.translationOrNull(key)) {
        return null
      }

      // Values a tooltip may reference with the standard @{...} placeholders.
      const timeZone = store.getters['setup/getAppConfiguration']?.timeZone

      return this.$trans(key, timeZone ? { timeZone } : undefined)
    },
    // A setting with a help article gets a clickable info icon. The URL lives
    // in the translations, so each language links to its own article.
    tooltipUrlFor (config: DraftConfiguration): string | null {
      return this.translationOrNull(`${config.trans}_tooltip_url`)
    },
    translationOrNull (key: string): string | null {
      const translations = store.state.setup.translations

      return translations && translations[key] ? translations[key] : null
    },
    updateGeneralConfiguration (key: string, value: null | string | boolean | Array<string> | Array<Record<string, any>> | Record<string, any>) {
      // A draft-level dispatch replaces the objects this panel renders from,
      // so buffered edits must reach the store first or the rebuild hides them.
      this.flushPendingProperties()
      store.dispatch('builder/updateDraftConfigurationProperty', { [key]: value })
    },
    updateConfigurationProperty (key: string, value: null | string | number | boolean | Array<string> | Array<Record<string, any>> | Record<string, any>, immediately = false) {
      // Every field edit in the builder lands here, so this is where a preview
      // learns which setting the user is on and can show the matching part of
      // the section instead of a state that setting never appears in.
      store.dispatch('builder/setEditedConfigurationKey', key)

      this.pendingProperties = {
        ...this.pendingProperties,
        [key]: value
      }

      window.clearTimeout(this.pendingFlushTimer)

      if (immediately) {
        this.flushPendingProperties()
      } else {
        this.pendingFlushTimer = window.setTimeout(() => {
          this.flushPendingProperties()
        }, 1000)
      }
    },
    flushPendingProperties (targetSection: Section | null = null, targetExternalConfiguration: Record<string, any> | null = null) {
      window.clearTimeout(this.pendingFlushTimer)

      if (Object.keys(this.pendingProperties).length === 0) {
        return
      }

      const result = this.pendingProperties
      this.pendingProperties = {}

      if (this.mode === DraftActionsType.CONFIGURATION) {
        store.dispatch('builder/updateDraftConfigurationProperty', result)
      } else if (this.mode === DraftActionsType.EXTERNAL_CONFIGURATION) {
        const target = targetExternalConfiguration || this.selectedExternalConfiguration
        store.dispatch('builder/updateDraftExternalConfigurationProperty', {
          property: result,
          externalConfigurationId: target?.id
        })
      } else if (this.mode === DraftActionsType.SECTION) {
        const sectionToUpdate = new SectionToUpdate(targetSection || this.selectedSection, this.draft, result)
        store.dispatch('builder/updateSectionConfigurationProperty', sectionToUpdate)
      }
    },
    updateConfigurationPropertyForSelect (key: string, value: any, selected: any) {
      value.value = selected.value
      this.updateConfigurationProperty(key, value, true)
    },
    refreshConfiguration (tools: Array<DraftConfigurationType>) {
      this.configuration = Array<DraftConfiguration>()
      tools.forEach((tool: DraftConfigurationType) => {
        let configurationRequested = null
        if (this.mode === DraftActionsType.CONFIGURATION) {
          configurationRequested = this.draft.configuration
        } else if (this.mode === DraftActionsType.EXTERNAL_CONFIGURATION) {
          configurationRequested = this.selectedExternalConfiguration
        } else if (this.mode === DraftActionsType.SECTION) {
          configurationRequested = this.selectedSection.configuration
        }

        for (const property in configurationRequested) {
          if (this.isLoginHidden && this.elementsRequireLogin.includes(property)) {
            continue
          }

          if (property.includes(`__${tool}__`)) {
            this.configuration.push({
              type: tool,
              key: property,
              trans: property.split(`__${tool}`)[0],
              value: configurationRequested[property],
              uuid: (this.selectedSection && this.selectedSection.uuid) ? this.selectedSection.uuid : uuidv4()
            })
          }
        }
      })

      // The loop above groups fields by type. An external configuration (a
      // dialog's texts, the cookie banner) reads better in the order its keys
      // were declared: a title next to its description, not all titles first.
      if (this.mode === DraftActionsType.EXTERNAL_CONFIGURATION && this.selectedExternalConfiguration) {
        const order = Object.keys(this.selectedExternalConfiguration)
        this.configuration.sort((a: DraftConfiguration, b: DraftConfiguration) => order.indexOf(a.key) - order.indexOf(b.key))
      }
    }
  }
})
